import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import Navigation from './Components/Navigation';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import ChallengeQuestionCard from '../EvaluationComponents/Shards/Questions/ChallengeQuestionCard';
import ComboBoxSelect from '../CreateEvaluationComponents/Shards/ComboBoxSelect';
import useSkillsAutocomplete from '../Utils/useSkillsAutocomplete';
import { CHALLENGE_QUESTION_TEMPLATES, QUESTION_TYPE_LABELS } from '../EvaluationComponents/Utils/QuestionTemplates';

const Library = () => {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState(null);
  
  // Applied filter states (used for API calls)
  const [appliedFilterType, setAppliedFilterType] = useState([]);
  const [appliedFilterSkills, setAppliedFilterSkills] = useState([]);
  const [appliedSkillsFilterMod, setAppliedSkillsFilterMod] = useState('OR');
  
  // Pending filter states (user selections before applying)
  const [filterType, setFilterType] = useState([]);
  const [filterSkills, setFilterSkills] = useState([]);
  const [skillsFilterMod, setSkillsFilterMod] = useState('OR');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 20;
  
  // Scroll behavior state
  const [showFilters, setShowFilters] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Skills autocomplete hook
  const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();
  
  // Get all unique question types from templates
  const questionTypes = Object.keys(CHALLENGE_QUESTION_TEMPLATES).sort();
  
  // Set document title
  useEffect(() => {
    document.title = "Library";
  }, []);
  
  // Handle scroll behavior to hide/show filters
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = Math.abs(currentScrollY - lastScrollY);
      
      // Only trigger if scroll difference is significant (reduces jitter)
      if (scrollDifference < 5) return;
      
      // Show filters when scrolling up, hide when scrolling down
      // But always show if at the top of the page
      if (currentScrollY < 100) {
        setShowFilters(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setShowFilters(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down
        setShowFilters(false);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);
  
  // Check if user is Superadmin, redirect if not
  useEffect(() => {
    if (user && user.role !== 'Superadmin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  // Fetch questions when page changes or APPLIED filters change
  useEffect(() => {
    if (user && user.role === 'Superadmin') {
      setCurrentPage(1);
    }
  }, [appliedFilterType, appliedFilterSkills, user]);
  
  // Fetch questions when page changes or filters change
  useEffect(() => {
    if (user && user.role === 'Superadmin') {
      fetchQuestions();
    }
  }, [currentPage, user, appliedFilterType, appliedFilterSkills]);
  
  // Handle applying filters
  const handleApplyFilters = () => {
    setAppliedFilterType(filterType);
    setAppliedFilterSkills(filterSkills);
    setAppliedSkillsFilterMod(skillsFilterMod);
  };
  
  // Handle clearing filters
  const handleClearFilters = () => {
    setFilterType([]);
    setFilterSkills([]);
  };
  
  // Check if there are filter changes to apply
  const hasFilterChanges = () => {
    const typeChanged = JSON.stringify(filterType.map(f => f.value).sort()) !== 
                        JSON.stringify(appliedFilterType.map(f => f.value).sort());
    const skillsChanged = JSON.stringify(filterSkills.map(f => f.value).sort()) !== 
                          JSON.stringify(appliedFilterSkills.map(f => f.value).sort());
    const toggleChanged = skillsFilterMod !== appliedSkillsFilterMod;
    return typeChanged || skillsChanged || toggleChanged;
  };
  
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters for server-side filtering
      const params = new URLSearchParams();
      if (appliedFilterType.length > 0) {
        const types = appliedFilterType.map(t => t.value);
        params.append('type', JSON.stringify(types));
      }
      if (appliedFilterSkills.length > 0) {
        const skills = appliedFilterSkills.map(s => s.value);
        params.append('skills', JSON.stringify(skills));
        params.append('skills_filter_mod', appliedSkillsFilterMod);
      }
      
      // Add pagination parameters
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      params.append('offset', offset.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());
      
      const url = `${API_ENDPOINTS.LIB_GET_CHALLENGE_QUESTIONS}${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Make GET request to fetch library questions
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Server responded with status: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data
      const data = await response.json();
      console.log('Library questions response:', data);
      
      // Check if response has valid data
      const count = data[0]?.count || 0;
      const items = count === 0 ? [] : (data[0]?.items || []);
      
      console.log('Library questions loaded:', items);
      console.log('total results:', count);
      
      setQuestions(items);
      setTotalCount(count);
      
    } catch (err) {
      console.error('Error loading library questions:', err);
      
      // If it's a 401 error, it's an expired session
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError('Failed to load library questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Don't render anything if not Superadmin
  if (!user || user.role !== 'Superadmin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]">
      <Navigation title="Library" showBackButton={false} />
      
      <div>
        {/* Filter Bar - Sticky with slide transition */}
        <div className={`sticky bg-white rounded-lg shadow-md mb-6 p-6 z-10 transition-all duration-500 ease-in-out ${
          showFilters ? 'top-[80px] opacity-100' : '-top-[300px] opacity-0'
        }`}>
          {/* Filters in one row */}
          <div className="flex gap-4 items-start mb-4">
            {/* Skills - flexible */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 h-9">
                <label className="block text-base font-semibold text-gray-700">
                  Skills
                </label>
                {/* AND/OR Toggle */}
                <div className="flex items-center gap-0.5 bg-gray-100 border border-gray-300 rounded p-0.5">
                  <button
                    onClick={() => setSkillsFilterMod('AND')}
                    className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                      skillsFilterMod === 'AND'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    AND
                  </button>
                  <button
                    onClick={() => setSkillsFilterMod('OR')}
                    className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                      skillsFilterMod === 'OR'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    OR
                  </button>
                </div>
              </div>
              <ComboBoxSelect
                value={filterSkills}
                onChange={(selectedOptions) => setFilterSkills(selectedOptions || [])}
                options={skillsOptions}
                placeholder="Search skills..."
                onInputChange={(inputValue, action) => {
                  if (action.action === 'input-change') {
                    if (inputValue && inputValue.length >= 2) {
                      searchSkills(inputValue);
                    } else {
                      clearSkills();
                    }
                  }
                }}
                isLoading={isLoadingSkills}
                isMulti={true}
                compact={true}
              />
            </div>
            
            {/* Question Type - flexible */}
            <div className="flex-1">
              <div className="mb-2 h-9 flex items-center">
                <label className="block text-base font-semibold text-gray-700">
                  Question Type
                </label>
              </div>
              <ComboBoxSelect
                value={filterType}
                onChange={(selectedOptions) => setFilterType(selectedOptions || [])}
                options={questionTypes.map(type => ({
                  value: type,
                  label: QUESTION_TYPE_LABELS[type] || type
                }))}
                placeholder="Search types..."
                isMulti={true}
                compact={true}
              />
            </div>
          </div>

          {/* Action buttons - centered at bottom, only show when there are changes */}
          {hasFilterChanges() && (
            <div className="flex justify-center gap-3">
              <button
                onClick={handleClearFilters}
                className="px-6 py-2 bg-white text-gray-700 border border-gray-300 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-8 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
                <p className="text-lg font-semibold text-gray-900">Loading library questions...</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
              <button
                onClick={fetchQuestions}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          )}
          
          {!loading && !error && questions && (
            <div>
              {/* Questions List */}
              {questions.length > 0 ? (
                <div className="space-y-6 mb-6">
                  {questions.map((item, index) => (
                    <div key={item.library_id} className="relative">
                      <ChallengeQuestionCard
                        question={{
                          ...item.item_json,
                          library_id: item.library_id,
                          item_status: item.item_status,
                          item_author: item.item_author
                        }}
                        index={index}
                        showAnswers={false}
                        onRegenerateQuestion={() => {}}
                        onLikeQuestion={() => {}}
                        onEditQuestion={null}
                        onDeleteQuestion={null}
                        onMoveUp={null}
                        onMoveDown={null}
                        totalQuestions={questions.length}
                        challengeId={null}
                        readOnly={true}
                        onAddToChallenge={null}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="mb-6">
                    <svg className="w-24 h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Questions Found</h3>
                  <p className="text-gray-600 mb-6 max-w-md text-center">
                    {(appliedFilterType.length > 0 || appliedFilterSkills.length > 0) ? (
                      <>No questions match the selected filters. Try adjusting your filter criteria to find more results.</>
                    ) : (
                      <>The library is currently empty. Questions will appear here once they are added to the library.</>
                    )}
                  </p>
                  {(appliedFilterType.length > 0 || appliedFilterSkills.length > 0) && (
                    <button
                      onClick={() => {
                        setFilterType([]);
                        setFilterSkills([]);
                        setAppliedFilterType([]);
                        setAppliedFilterSkills([]);
                      }}
                      className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Clear All Filters
                    </button>
                  )}
                </div>
              )}
              
              {/* Pagination Controls */}
              {totalCount > 0 && (
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  {/* Results info */}
                  <div className="text-sm text-gray-600">
                    Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalCount)}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} question{totalCount !== 1 ? 's' : ''}
                  </div>
                  
                  {/* Pagination buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {(() => {
                        const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
                        const pages = [];
                        const maxVisible = 5;
                        
                        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                        
                        if (endPage - startPage + 1 < maxVisible) {
                          startPage = Math.max(1, endPage - maxVisible + 1);
                        }
                        
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                currentPage === i
                                  ? 'bg-purple-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }
                        
                        return pages;
                      })()}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / ITEMS_PER_PAGE), prev + 1))}
                      disabled={currentPage >= Math.ceil(totalCount / ITEMS_PER_PAGE)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Library;
